import React, { memo, useMemo } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useFormikContext } from 'formik';
import { Button } from '@mui/material';
import { useToggle } from 'react-use';
import TextField from '@mui/material/TextField';

import { DropDownOption } from '../../../../../../types';
import { AddressCountryString } from '../../../../../../api/schemas/addressSchema';

import generatePdfStyle from './generatePdfStyle';
import { AddressDialog } from './AddressDialog';

const startFieldName = `agent.`;

export type AgentBeneficiaryState = {
    agent: AddressCountryString & { name: string; _id: string; generalLedgerAccountNumber?: string };
};

type Props<T> = {
    options?: T[];
    hideBeneficiaryField?: boolean;
    countryOptions: DropDownOption[];
};

const AgentBeneficiaryFormField = <T extends object>(props: Props<T>) => {
    const { options, countryOptions, hideBeneficiaryField = false } = props;
    const classes = generatePdfStyle();
    const [addressOpen, toggleAddressOpen] = useToggle(false);

    const formProps = useFormikContext<AgentBeneficiaryState>();
    const { values } = formProps;

    const addressDetailsText = useMemo(() => {
        const { address, province, country, city, postalCode } = values.agent || {} as any;
        const countryName = typeof country === 'string' ? country
            : typeof country === 'number' ? (countryOptions.find((c) => c._id === country)?.name || '')
            : country?.name || '';
        return [address, city, countryName, postalCode].filter(Boolean).join(', ');
    }, [values.agent, countryOptions]);

    return (
        <>
            <Stack spacing={2}>
                <TextField
                    label="Beneficiary"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={values.agent?.name || ''}
                    disabled
                />
                <TextField
                    label="Address Details"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={addressDetailsText}
                    disabled
                />
                <Box>
                    <Button variant="outlined" color="primary" size="small" onClick={() => toggleAddressOpen(true)}>
                        Edit
                    </Button>
                </Box>
                {/* "For" field — free text per TB spec (displayed on the document) */}
                {!hideBeneficiaryField && (
                    <TextField
                        name="for"
                        label="For"
                        variant="outlined"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={(formProps.values as any).for || ''}
                        onChange={(e) => formProps.setFieldValue('for', e.target.value)}
                    />
                )}
            </Stack>
            {addressOpen && (
                <AddressDialog
                    countryOptions={countryOptions}
                    startFieldName={startFieldName}
                    addressOpen={addressOpen}
                    toggleAddressOpen={toggleAddressOpen}
                />
            )}
        </>
    );
};

export default memo(AgentBeneficiaryFormField) as typeof AgentBeneficiaryFormField;
